import { useState, useEffect, useRef } from "react";
import {
  Plus, Trash2, ChevronUp, ChevronDown, ArrowLeft, Save,
  Image as ImageIcon, Type, List as ListIcon, Quote as QuoteIcon,
  MessageSquareWarning, MousePointerClick, Minus, Upload, FileText,
  HelpCircle, Video as VideoIcon, Table as TableIcon, Send, User as UserIcon,
} from "lucide-react";
import BlockRenderer from "../lib/blockRenderer";
import { listPosts, createPost, updatePost, deletePost } from "../../../apis/services/pgApi";
import { importDocxToBlocks } from "../lib/docxImport";

// ─────────────────────────────────────────────────────────────────────────────
// This dashboard writes/reads the exact same `blocks` schema that blog.js
// renders (see blockRenderer.js for the full schema doc). Nothing here is
// blog.js-specific — all reads/writes go through src/lib/blogApi.js, which
// talks to the live backend (GET /blog, POST /blog/create).
// ─────────────────────────────────────────────────────────────────────────────

// ── TEMPORARY admin gate ──────────────────────────────────────────────────
// There's no admin role/login in the main app yet, so this page checks its
// own hardcoded credentials before showing anything. Auth lives only in
// sessionStorage (cleared when the tab/browser closes) — nothing is sent
// anywhere, this is just a lock on the door.
//
// TODO: replace ADMIN_USERNAME/ADMIN_PASSWORD + the check in AdminLoginGate
// with a real POST /blog/admin-login (or similar) call once that API exists.
const ADMIN_USERNAME = "aapkapgadmin";
const ADMIN_PASSWORD = "aapkapgadmin";
const ADMIN_SESSION_KEY = "aapkapg_blog_admin_authed";

function AdminLoginGate({ children }) {
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem(ADMIN_SESSION_KEY) === "true"
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Keep this page out of search engines regardless of how it's reached.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, "true");
      setAuthed(true);
      setErrorMsg("");
    } else {
      setErrorMsg("Incorrect username or password.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setAuthed(false);
    setUsername("");
    setPassword("");
  };

  if (!authed) {
    return (
      <div className="ed-login">
        <form className="ed-login__card" onSubmit={handleSubmit}>
          <h1 className="ed-login__title">Blog admin</h1>
          <p className="ed-login__subtext">Sign in to manage blog posts.</p>
          <label className="ed-login__label">
            Username
            <input
              className="ed-login__input"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </label>
          <label className="ed-login__label">
            Password
            <input
              className="ed-login__input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {errorMsg && <p className="ed-login__error">{errorMsg}</p>}
          <button type="submit" className="ed-save-btn ed-login__submit">Sign in</button>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="ed-login-bar">
        <span className="ed-login-bar__label">Signed in as {ADMIN_USERNAME}</span>
        <button type="button" className="ed-secondary-btn ed-login-bar__logout" onClick={handleLogout}>
          Log out
        </button>
      </div>
      {children}
    </>
  );
}

const uid = () => Math.random().toString(36).slice(2, 10);

const BLOCK_DEFAULTS = {
  heading:   { type: "heading", level: 2, content: "" },
  paragraph: { type: "paragraph", content: "" },
  list:      { type: "list", style: "bullet", items: [""] },
  image:     { type: "image", src: "", alt: "", caption: "", width: "100", align: "center" },
  quote:     { type: "quote", content: "", attribution: "" },
  callout:   { type: "callout", variant: "tip", content: "" },
  cta:       { type: "cta", heading: "", subtext: "", buttonText: "", buttonLink: "" },
  faq:       { type: "faq", title: "Frequently asked questions", items: [{ q: "", a: "" }] },
  video:     { type: "video", provider: "youtube", videoId: "", caption: "" },
  table:     { type: "table", caption: "", headers: ["Column 1", "Column 2", "Column 3"], rows: [["", "", ""], ["", "", ""]] },
  divider:   { type: "divider" },
};

const BLOCK_META = {
  heading:   { label: "Heading",   icon: Type },
  paragraph: { label: "Paragraph", icon: Type },
  list:      { label: "List",      icon: ListIcon },
  image:     { label: "Image",     icon: ImageIcon },
  quote:     { label: "Quote",     icon: QuoteIcon },
  callout:   { label: "Callout",   icon: MessageSquareWarning },
  cta:       { label: "Call to action", icon: MousePointerClick },
  faq:       { label: "FAQ",       icon: HelpCircle },
  video:     { label: "Video",     icon: VideoIcon },
  table:     { label: "Table",     icon: TableIcon },
  divider:   { label: "Divider",   icon: Minus },
};

const EMPTY_POST = () => ({
  slug: "", title: "", excerpt: "", author: "AapkaPG Team",
  authorImage: "", authorBio: "",
  date: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
  readTime: "5 min read", category: "PG Owners", featured: false,
  published: false, blocks: [],
});

function slugify(str) {
  return str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

// Used while the user is actively typing in the slug field — keeps a trailing
// "-" in place instead of stripping it on every keystroke (slugify() strips
// it, which made it impossible to type a dash: it got deleted before the next
// character landed). Collapses runs of invalid chars into a single dash but
// doesn't trim the ends.
function slugifyLive(str) {
  return str.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-{2,}/g, "-");
}

// ─── BLOCK FIELD EDITORS ──────────────────────────────────────────────────────
function BlockFields({ block, onChange }) {
  const set = (patch) => onChange({ ...block, ...patch });

  if (block.type === "heading") {
    return (
      <div className="ed-fields">
        <select className="ed-input ed-input--sm" value={block.level} onChange={e => set({ level: Number(e.target.value) })}>
          <option value={2}>Heading 2 (section)</option>
          <option value={3}>Heading 3 (sub-section)</option>
        </select>
        <input className="ed-input" placeholder="Heading text" value={block.content}
          onChange={e => set({ content: e.target.value })} />
      </div>
    );
  }

  if (block.type === "paragraph") {
    return (
      <textarea className="ed-input ed-textarea" rows={3} placeholder="Paragraph text — use **bold** for emphasis"
        value={block.content} onChange={e => set({ content: e.target.value })} />
    );
  }

  if (block.type === "list") {
    const itemsText = block.items.join("\n");
    return (
      <div className="ed-fields">
        <select className="ed-input ed-input--sm" value={block.style} onChange={e => set({ style: e.target.value })}>
          <option value="bullet">Bullet points</option>
          <option value="numbered">Numbered</option>
          <option value="checklist">Checklist</option>
        </select>
        <textarea className="ed-input ed-textarea" rows={4} placeholder={"One item per line"}
          value={itemsText}
          onChange={e => set({ items: e.target.value.split("\n") })} />
      </div>
    );
  }

  if (block.type === "image") {
    const onFile = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => set({ src: reader.result });
      reader.readAsDataURL(file);
    };
    return (
      <div className="ed-fields">
        <div className="ed-image-row">
          <input className="ed-input" placeholder="Image URL (or upload →)" value={block.src.startsWith("data:") ? "(uploaded file)" : block.src}
            onChange={e => set({ src: e.target.value })} />
          <label className="ed-upload-btn">
            <Upload className="ed-icon-sm" /> Upload
            <input type="file" accept="image/*" onChange={onFile} hidden />
          </label>
        </div>
        {block.src && (
          <div className="bl-root"><img src={block.src} alt="" className="ed-image-thumb" /></div>
        )}
        <input className="ed-input" placeholder="Alt text (for SEO/accessibility)" value={block.alt}
          onChange={e => set({ alt: e.target.value })} />
        <input className="ed-input" placeholder="Caption (optional)" value={block.caption}
          onChange={e => set({ caption: e.target.value })} />
        <div className="ed-image-row">
          <div>
            <label className="ed-label">Width</label>
            <select className="ed-input ed-input--sm" value={block.width} onChange={e => set({ width: e.target.value })}>
              <option value="25">25%</option>
              <option value="50">50%</option>
              <option value="75">75%</option>
              <option value="100">Full width</option>
            </select>
          </div>
          <div>
            <label className="ed-label">Align / wrap</label>
            <select className="ed-input ed-input--sm" value={block.align} onChange={e => set({ align: e.target.value })}>
              <option value="left">Left (text wraps right)</option>
              <option value="right">Right (text wraps left)</option>
              <option value="center">Center (no wrap)</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  if (block.type === "quote") {
    return (
      <div className="ed-fields">
        <textarea className="ed-input ed-textarea" rows={2} placeholder="Quote text"
          value={block.content} onChange={e => set({ content: e.target.value })} />
        <input className="ed-input" placeholder="Attribution (optional)" value={block.attribution}
          onChange={e => set({ attribution: e.target.value })} />
      </div>
    );
  }

  if (block.type === "callout") {
    return (
      <div className="ed-fields">
        <select className="ed-input ed-input--sm" value={block.variant} onChange={e => set({ variant: e.target.value })}>
          <option value="tip">Tip</option>
          <option value="warning">Warning / red flag</option>
          <option value="info">Info</option>
        </select>
        <textarea className="ed-input ed-textarea" rows={2} placeholder="Callout text"
          value={block.content} onChange={e => set({ content: e.target.value })} />
      </div>
    );
  }

  if (block.type === "cta") {
    return (
      <div className="ed-fields">
        <input className="ed-input" placeholder="Heading" value={block.heading} onChange={e => set({ heading: e.target.value })} />
        <input className="ed-input" placeholder="Subtext" value={block.subtext} onChange={e => set({ subtext: e.target.value })} />
        <div className="ed-image-row">
          <input className="ed-input" placeholder="Button text" value={block.buttonText} onChange={e => set({ buttonText: e.target.value })} />
          <input className="ed-input" placeholder="Button link" value={block.buttonLink} onChange={e => set({ buttonLink: e.target.value })} />
        </div>
      </div>
    );
  }

if (block.type === "faq") {
    const items = block.items && block.items.length ? block.items : [{ q: "", a: "" }];
    const setItem = (i, patch) => set({ items: items.map((it, ii) => ii === i ? { ...it, ...patch } : it) });
    const addItem = () => set({ items: [...items, { q: "", a: "" }] });
    const removeItem = (i) => set({ items: items.filter((_, ii) => ii !== i) });
    return (
      <div className="ed-fields">
        <input className="ed-input" placeholder="Section title (optional)" value={block.title || ""}
          onChange={e => set({ title: e.target.value })} />
        {items.map((it, i) => (
          <div key={i} className="ed-fields" style={{ padding: "0.5rem", border: "1px solid var(--bl-border, #e5e7eb)", borderRadius: "0.5rem" }}>
            <div className="ed-image-row" style={{ alignItems: "center" }}>
              <input className="ed-input" placeholder={`Question ${i + 1}`} value={it.q}
                onChange={e => setItem(i, { q: e.target.value })} />
              <button type="button" className="ed-block__delete" onClick={() => removeItem(i)} title="Remove Q&A">
                <Trash2 className="ed-icon-sm" />
              </button>
            </div>
            <textarea className="ed-input ed-textarea" rows={2} placeholder="Answer — **bold** allowed"
              value={it.a} onChange={e => setItem(i, { a: e.target.value })} />
          </div>
        ))}
        <button type="button" className="ed-add-btn" onClick={addItem}>
          <Plus className="ed-icon-sm" /> Add question
        </button>
      </div>
    );
  }

  if (block.type === "video") {
    // Accept a raw YouTube/Vimeo URL and auto-extract the ID for the SEO team.
    const parseUrl = (url) => {
      if (!url) return null;
      const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
      if (yt) return { provider: "youtube", videoId: yt[1] };
      const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vm) return { provider: "vimeo", videoId: vm[1] };
      return null;
    };
    const onUrl = (e) => {
      const val = e.target.value;
      const parsed = parseUrl(val);
      if (parsed) set(parsed); else set({ videoId: val });
    };
    return (
      <div className="ed-fields">
        <div className="ed-image-row">
          <div>
            <label className="ed-label">Provider</label>
            <select className="ed-input ed-input--sm" value={block.provider}
              onChange={e => set({ provider: e.target.value })}>
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="ed-label">Video URL or ID</label>
            <input className="ed-input" placeholder="Paste YouTube/Vimeo URL — ID auto-extracts"
              value={block.videoId} onChange={onUrl} />
          </div>
        </div>
        <input className="ed-input" placeholder="Caption (optional)" value={block.caption || ""}
          onChange={e => set({ caption: e.target.value })} />
      </div>
    );
  }

  if (block.type === "table") {
    // Normalize headers/rows to a consistent column count before rendering.
    // After a .docx import (or any manual edit) headers and row widths can
    // drift apart — e.g. headers.length === 1 while every row already holds
    // 3 filled cells. Rendering the raw arrays then hides the extra, already-
    // populated columns behind the "+" button. Padding both to
    // max(headers.length, longest row, 1) makes every existing cell visible
    // immediately, with "+" only ever adding a genuinely new blank column.
    const rawHeaders = block.headers?.length ? block.headers : [""];
    const rawRows = block.rows?.length ? block.rows : [Array(rawHeaders.length).fill("")];
    const cols = Math.max(rawHeaders.length, ...rawRows.map((r) => r.length), 1);
    const headers = Array.from({ length: cols }, (_, i) => rawHeaders[i] ?? `Column ${i + 1}`);
    const rows = rawRows.map((row) => Array.from({ length: cols }, (_, i) => row[i] ?? ""));

    const setHeader = (ci, val) =>
      set({ headers: headers.map((h, i) => (i === ci ? val : h)) });

    const setCell = (ri, ci, val) =>
      set({
        rows: rows.map((row, i) =>
          i === ri ? row.map((c, j) => (j === ci ? val : c)) : row
        ),
      });

    const addColumn = () =>
      set({
        headers: [...headers, `Column ${cols + 1}`],
        rows: rows.map((r) => [...r, ""]),
      });

    const removeColumn = (ci) => {
      if (cols <= 1) return;
      set({
        headers: headers.filter((_, i) => i !== ci),
        rows: rows.map((r) => r.filter((_, i) => i !== ci)),
      });
    };

    const addRow = () =>
      set({ rows: [...rows, Array(cols).fill("")] });

    const removeRow = (ri) => {
      if (rows.length <= 1) return;
      set({ rows: rows.filter((_, i) => i !== ri) });
    };

    return (
      <div className="ed-fields">
        <input className="ed-input" placeholder="Table caption (optional)"
          value={block.caption || ""} onChange={e => set({ caption: e.target.value })} />

        <div className="ed-table-editor">
          <div className="ed-table-editor__scroll">
            <table className="ed-table-editor__grid">
              <thead>
                <tr>
                  {headers.map((h, ci) => (
                    <th key={ci}>
                      <div className="ed-table-editor__cell">
                        <input className="ed-input ed-input--sm ed-table-editor__input ed-table-editor__input--head"
                          placeholder={`Header ${ci + 1}`} value={h}
                          onChange={e => setHeader(ci, e.target.value)} />
                        <button type="button" className="ed-table-editor__mini" title="Remove column"
                          disabled={cols <= 1} onClick={() => removeColumn(ci)}>
                          <Trash2 className="ed-icon-sm" />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="ed-table-editor__addcol">
                    <button type="button" className="ed-table-editor__mini" title="Add column" onClick={addColumn}>
                      <Plus className="ed-icon-sm" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {Array.from({ length: cols }).map((_, ci) => (
                      <td key={ci}>
                        <input className="ed-input ed-input--sm ed-table-editor__input"
                          placeholder="—"
                          value={row[ci] || ""}
                          onChange={e => setCell(ri, ci, e.target.value)} />
                      </td>
                    ))}
                    <td className="ed-table-editor__addcol">
                      <button type="button" className="ed-table-editor__mini" title="Remove row"
                        disabled={rows.length <= 1} onClick={() => removeRow(ri)}>
                        <Trash2 className="ed-icon-sm" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="ed-add-btn ed-table-editor__addrow" onClick={addRow}>
            <Plus className="ed-icon-sm" /> Add row
          </button>
        </div>
      </div>
    );
  }

  return null; // divider — no fields
}

// ─── SINGLE BLOCK ROW ─────────────────────────────────────────────────────────
function BlockRow({ block, index, total, onChange, onDelete, onMove }) {
  const Icon = BLOCK_META[block.type]?.icon || Type;
  return (
    <div className="ed-block">
      <div className="ed-block__header">
        <span className="ed-block__type"><Icon className="ed-icon-sm" /> {BLOCK_META[block.type]?.label}</span>
        <div className="ed-block__actions">
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} title="Move up"><ChevronUp className="ed-icon-sm" /></button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(1)} title="Move down"><ChevronDown className="ed-icon-sm" /></button>
          <button type="button" onClick={onDelete} title="Delete block" className="ed-block__delete"><Trash2 className="ed-icon-sm" /></button>
        </div>
      </div>
      <BlockFields block={block} onChange={onChange} />
    </div>
  );
}

// ─── POST EDITOR (metadata + blocks + live preview) ──────────────────────────
function PostEditor({ initialPost, existingSlugs, onSave, saving, onDelete, deleting, onCancel }) {
  const [post, setPost] = useState(initialPost);
  const [slugTouched, setSlugTouched] = useState(!!initialPost.slug);

  const setField = (patch) => setPost(p => ({ ...p, ...patch }));

  const onTitleChange = (title) => {
    setField({ title, ...(slugTouched ? {} : { slug: slugify(title) }) });
  };

  const addBlock = (type) => {
    setPost(p => ({ ...p, blocks: [...p.blocks, { ...BLOCK_DEFAULTS[type], _id: uid() }] }));
  };
  const updateBlock = (i, next) => {
    setPost(p => ({ ...p, blocks: p.blocks.map((b, bi) => bi === i ? next : b) }));
  };
  const deleteBlock = (i) => {
    setPost(p => ({ ...p, blocks: p.blocks.filter((_, bi) => bi !== i) }));
  };
  const moveBlock = (i, dir) => {
    setPost(p => {
      const blocks = [...p.blocks];
      const j = i + dir;
      if (j < 0 || j >= blocks.length) return p;
      [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
      return { ...p, blocks };
    });
  };

  const slugConflict = post.slug && existingSlugs.includes(post.slug);
  const canSave = post.title.trim() && post.slug.trim() && !slugConflict && !saving;

  const onAuthorImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setField({ authorImage: reader.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Helper function to check if authorImage is a valid image (data URL or URL)
  const isValidAuthorImage = (image) => {
    if (!image) return false;
    // Check if it's a data URL (uploaded file) or a proper URL
    return image.startsWith('data:') || image.startsWith('http://') || image.startsWith('https://');
  };

  return (
    <div className="ed-editor">
      <div className="ed-editor__form">
        <button type="button" className="ed-back" onClick={onCancel}><ArrowLeft className="ed-icon-sm" /> All posts</button>

        {initialPost._imported && (
          <div className="ed-import-banner">
            Imported from Word — this is a best-effort draft. Check that headings, tips, and lists were split out correctly before saving.
          </div>
        )}

        <h2 className="ed-h2">Post details</h2>
        <div className="ed-fields">
          <input className="ed-input" placeholder="Title" value={post.title} onChange={e => onTitleChange(e.target.value)} />
          <input className="ed-input" placeholder="URL slug" value={post.slug}
            onChange={e => { setSlugTouched(true); setField({ slug: slugifyLive(e.target.value) }); }}
            onBlur={e => setField({ slug: slugify(e.target.value) })} />
          {slugConflict && <p className="ed-error">A post with this slug already exists.</p>}
          <textarea className="ed-input ed-textarea" rows={2} placeholder="Excerpt (shown on the blog list card)"
            value={post.excerpt} onChange={e => setField({ excerpt: e.target.value })} />
          <div className="ed-image-row">
            <input className="ed-input" placeholder="Author" value={post.author} onChange={e => setField({ author: e.target.value })} />
            <input className="ed-input" placeholder="Category" value={post.category} onChange={e => setField({ category: e.target.value })} />
          </div>
          <div className="ed-image-row">
            <input className="ed-input" placeholder="Date" value={post.date} onChange={e => setField({ date: e.target.value })} />
            <input className="ed-input" placeholder="Read time" value={post.readTime} onChange={e => setField({ readTime: e.target.value })} />
          </div>
          <label className="ed-checkbox">
            <input type="checkbox" checked={!!post.featured} onChange={e => setField({ featured: e.target.checked })} />
            Feature this post at the top of the blog
          </label>
        </div>

        <h2 className="ed-h2"><UserIcon className="ed-icon-sm" /> Author</h2>
        <p className="ed-subtext">Shown in an "About the author" card at the end of the article. If no photo is uploaded, the author's first initial is shown instead.</p>
        <div className="ed-fields">
          <div className="ed-author-editor">
            <div className="ed-author-editor__avatar">
              {isValidAuthorImage(post.authorImage)
                ? <img src={post.authorImage} alt={post.author || ""} />
                : <span>{(post.author || "?").trim().charAt(0).toUpperCase()}</span>}
            </div>
            <div className="ed-author-editor__controls">
              <div className="ed-image-row">
                <input className="ed-input" placeholder="Author image URL (optional)"
                  value={post.authorImage && post.authorImage.startsWith("data:") ? "(uploaded file)" : (post.authorImage || "")}
                  onChange={e => {
                    const value = e.target.value;
                    // Only set if it's a valid URL or empty string
                    if (value === "" || isValidAuthorImage(value)) {
                      setField({ authorImage: value });
                    } else {
                      // If it's not a valid URL, treat it as empty (don't set the author name as image)
                      setField({ authorImage: "" });
                    }
                  }} />
                <label className="ed-upload-btn">
                  <Upload className="ed-icon-sm" /> Upload
                  <input type="file" accept="image/*" onChange={onAuthorImageFile} hidden />
                </label>
              </div>
              {post.authorImage && isValidAuthorImage(post.authorImage) && (
                <button type="button" className="ed-secondary-btn" onClick={() => setField({ authorImage: "" })}>
                  <Trash2 className="ed-icon-sm" /> Remove photo
                </button>
              )}
            </div>
          </div>
          <textarea className="ed-input ed-textarea" rows={3}
            placeholder="Short author bio (optional) — 1-2 sentences about the author"
            value={post.authorBio || ""} onChange={e => setField({ authorBio: e.target.value })} />
        </div>

        <h2 className="ed-h2">Content blocks</h2>
        <div className="ed-blocks">
          {post.blocks.length === 0 && <p className="ed-empty">No blocks yet — add one below.</p>}
          {post.blocks.map((b, i) => (
            <BlockRow key={b._id || i} block={b} index={i} total={post.blocks.length}
              onChange={(next) => updateBlock(i, next)}
              onDelete={() => deleteBlock(i)}
              onMove={(dir) => moveBlock(i, dir)} />
          ))}
        </div>

        <div className="ed-add-row">
          {Object.entries(BLOCK_META).map(([type, meta]) => {
            const Icon = meta.icon;
            return (
              <button type="button" key={type} className="ed-add-btn" onClick={() => addBlock(type)}>
                <Icon className="ed-icon-sm" /> {meta.label}
              </button>
            );
          })}
        </div>

        {/* Dedicated action bar — its own card, well below the block tiles so
            the save buttons never collide with the last block being edited. */}
        <div className="ed-action-bar">
          <div className="ed-action-bar__status">
            {post.published
              ? <span className="ed-status-pill ed-status-pill--live">● Published</span>
              : <span className="ed-status-pill ed-status-pill--draft">● Draft</span>}
            {slugConflict && <span className="ed-error" style={{ marginLeft: "0.75rem" }}>Slug already in use</span>}
          </div>
          <div className="ed-action-bar__buttons">
            {post._blogId != null && (
              <button type="button" className="ed-secondary-btn ed-danger-btn" disabled={saving || deleting}
                onClick={() => onDelete(post)}>
                <Trash2 className="ed-icon-sm" /> {deleting ? "Deleting…" : "Delete post"}
              </button>
            )}
            <button type="button" className="ed-secondary-btn" disabled={!canSave}
              onClick={() => onSave({ ...post, published: false })}>
              <Save className="ed-icon-sm" /> {saving ? "Saving…" : "Save draft"}
            </button>
            <button type="button" className="ed-save-btn" disabled={!canSave}
              onClick={() => onSave({ ...post, published: true })}>
              <Send className="ed-icon-sm" /> {saving ? "Saving…" : post.published ? "Update published post" : "Publish post"}
            </button>
          </div>
        </div>
      </div>

      <div className="ed-editor__preview">
        <div className="ed-preview__label">Live preview</div>
        <div className="bl-root ed-preview__frame">
          <span className="bl-badge bl-detail-badge">{post.category || "Category"}</span>
          <h1 className="bl-fluid-4xl bl-fw-800 bl-detail-title">{post.title || "Untitled post"}</h1>
          <div className="bl-post-meta">
            <span>{post.author}</span><span>{post.date}</span><span>{post.readTime}</span>
          </div>
          <article className="bl-article-body" style={{ marginTop: "1.5rem" }}>
            <BlockRenderer blocks={post.blocks} />
          </article>
        </div>
      </div>
    </div>
  );
}

// ─── POST LIST ────────────────────────────────────────────────────────────────
function PostList({ posts, onEdit, onNew, onImportFile, importing, onDelete, deletingId }) {
  const fileInputRef = useRef(null);
  return (
    <div className="ed-list">
      <div className="ed-list__header">
        <h1 className="ed-h1">Blog dashboard</h1>
        <div className="ed-list__actions">
          <button type="button" className="ed-secondary-btn" disabled={importing} onClick={() => fileInputRef.current?.click()}>
            <FileText className="ed-icon-sm" /> {importing ? "Importing…" : "Import Word doc"}
          </button>
          <input ref={fileInputRef} type="file" accept=".docx" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onImportFile(f); e.target.value = ""; }} />
          <button type="button" className="ed-save-btn" onClick={onNew}><Plus className="ed-icon-sm" /> New post</button>
        </div>
      </div>
      <p className="ed-subtext">Posts are loaded from and saved to the live API.</p>
      <div className="ed-list__items">
        {posts.map(p => (
          <div key={p._blogId ?? p.slug} className="ed-list-item">
            <div>
              <div className="ed-list-item__title">
                {p.title}
                {p.featured && <span className="bl-badge" style={{ marginLeft: "0.5rem" }}>Featured</span>}
                {p.published === false && <span className="ed-status-pill ed-status-pill--draft" style={{ marginLeft: "0.5rem" }}>Draft</span>}
              </div>
              <div className="ed-list-item__meta">/{p.slug} · {p.blocks?.length || 0} blocks</div>
            </div>
            <div className="ed-list-item__actions">
              <button type="button" className="ed-secondary-btn" onClick={() => onEdit(p)}>Edit</button>
              {p._blogId != null && (
                <button type="button" className="ed-secondary-btn ed-danger-btn"
                  disabled={deletingId === p._blogId}
                  onClick={() => onDelete(p)}>
                  <Trash2 className="ed-icon-sm" /> {deletingId === p._blogId ? "Deleting…" : "Delete"}
                </button>
              )}
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="ed-empty">No posts yet. Click "New post" to create one.</p>}
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function BlogEditor() {
  return (
    <AdminLoginGate>
      <BlogEditorContent />
    </AdminLoginGate>
  );
}

function BlogEditorContent() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view, object = editing
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setPosts(await listPosts());
    } catch (err) {
      setError(err.message || "Couldn't load posts from the server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async (post) => {
    const { _imported, _blogId, _link, _createdDate, _userId, ...clean } = post;
    setSaving(true);
    try {
      if (_blogId != null) {
        // Existing post — edit in place instead of creating a duplicate.
        await updatePost(_blogId, clean);
      } else {
        await createPost(clean);
      }
      await refresh();
      setEditing(null);
    } catch (err) {
      window.alert(`Couldn't save this post: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (post._blogId == null) return;
    if (!window.confirm(`Delete "${post.title || post.slug}"? This can't be undone.`)) return;
    setDeletingId(post._blogId);
    try {
      await deletePost(post._blogId);
      await refresh();
      setEditing(null);
    } catch (err) {
      window.alert(`Couldn't delete this post: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const { blocks, guessedTitle, warnings } = await importDocxToBlocks(file);
      const draft = {
        ...EMPTY_POST(),
        title: guessedTitle || file.name.replace(/\.docx$/i, ""),
        slug: slugify(guessedTitle || file.name.replace(/\.docx$/i, "")),
        blocks: blocks.map(b => ({ ...b, _id: Math.random().toString(36).slice(2, 10) })),
        _imported: true,
      };
      if (warnings?.length) console.warn("Docx import notes:", warnings);
      setEditing(draft);
    } catch (err) {
      window.alert("Couldn't read that file — make sure it's a .docx (not .doc or .pdf).");
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="ed-root">
      {editing ? (
        <PostEditor
          initialPost={editing}
          existingSlugs={posts.map(p => p.slug).filter(s => s !== editing.slug)}
          onSave={handleSave}
          saving={saving}
          onDelete={handleDelete}
          deleting={editing._blogId != null && deletingId === editing._blogId}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <>
          {error && <p className="ed-error">{error}</p>}
          {loading && <p className="ed-empty">Loading posts…</p>}
          <PostList
            posts={posts}
            onEdit={setEditing}
            onNew={() => setEditing(EMPTY_POST())}
            onImportFile={handleImportFile}
            importing={importing}
            onDelete={handleDelete}
            deletingId={deletingId}
          />
        </>
      )}
    </div>
  );
}